<template>
	<div class="hotel-info__container">
		<div class="hotel-info">
			<div class="hotel-info__header">
				<h1>{{ hotelDetails.Hotel.Name }}</h1>
				<HotelStars
					class="hotel-info__stars"
					:show-suns="showSuns"
					:rating="rating"
				/>
			</div>
			<div class="hotel-info__address">
				<BaseIcon
					class="hotel-info__icon"
					name="marker"
					viewBox="0 2 20 27"
				/>
				<span>{{ address }}</span>
				<a
					class="hotel-info__link"
					v-bind="gaSelectContentAttr({ cId: 'hotelinfo_map_link' })"
					@click.prevent="openMap"
				>
					Auf Karte anzeigen
				</a>
			</div>
		</div>
		<Teleport
			defer
			:to="isMobile ? '#hotel-gallery' : '.hotel-info__container'"
		>
			<CustomerReviewsRatings
				class="hotel-info__ratings"
				:before-gallery="!isMobile"
				:after-gallery="isMobile"
				:rating="rating"
				:rating-count="hotelDetails.RatingCount"
				:recommendation-rate="hotelDetails.RatingRecommendationRate"
				:rating-label="ratingDescription"
				:rating-overall-value="hotelDetails.RatingOverallValue"
			/>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import HotelStars from '@lmt-rpb/HotelStars/HotelStars.vue';
import CustomerReviewsRatings from '@lmt-rpb/CustomerReviewsRatings/CustomerReviewsRatings.vue';
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import type { HotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { computed } from 'vue';
import { isClientGermany } from '@utils/environmentUtils';
import { EventBus } from '@global-js/event-bus';
import { ratingText, isMinimumRating } from '@utils/utils';
import { gaSelectContentAttr } from 'src/utils/analytics';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	hotel: HotelData;
}

const props = withDefaults(defineProps<Props>(), {});
const showSuns = isClientGermany();
const { isMobile } = storeToRefs(useBreakpointStore());
const hotelDetails = computed(() => ({
	Hotel: props.hotel.Hotel,
	Location: props.hotel.Location,
	RatingCount: props.hotel.RatingCount,
	RatingOverallValue: props.hotel.RatingOverallValue,
	RatingRecommendationRate: props.hotel.RatingRecommendationRate,
}));

const ratingDescription = computed(() => {
	let rating = '';
	if (isMinimumRating(hotelDetails.value.RatingOverallValue)) {
		rating = `${ratingText(hotelDetails.value.RatingOverallValue)} – `;
	}
	return rating;
});

const rating = Number(hotelDetails.value.Hotel?.Category);

const address = hotelDetails.value.Location.Address ? hotelDetails.value.Location.Address.join('; ') : '';

const openMap = () => {
	EventBus.$emit('hotel:openMap');
};
</script>

<style lang="scss" scoped>
* {
	padding: 0;
	margin: 0;
}

.hotel-info {
	&__container {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		width: 100%;

		@media (min-width: $breakpoint-small) {
			margin: 0.625rem 0 0.5rem;
			align-items: center;

			@media (min-width: $breakpoint-small) {
				min-width: 66%;
			}
		}

		> :first-child {
			min-width: 50%;
		}
	}

	&__header {
		h1 {
			display: inline;
			text-align: left;
			margin-right: 0.5rem;
			font-size: $font-medium-2;
			line-height: 1.2;

			@media (min-width: $breakpoint-small) {
				font-size: $font-large-4;
				line-height: 1.375;
			}

			@media (min-width: $breakpoint-extralarge) {
				font-size: $font-large-2;
				line-height: 1.4;
			}
		}
	}

	&__stars {
		display: block;
		position: relative;
		text-align: left;
		white-space: nowrap;
		width: fit-content;

		@media (min-width: $breakpoint-mobiletabs) {
			display: inline-block;
			width: auto;
		}

		@media (min-width: $breakpoint-small) {
			top: -1rem;
		}
	}

	&__address {
		display: flex;
		align-items: flex-end;
		font-size: $font-small-3;

		@media (max-width: $breakpoint-verysmall - 1px) {
			margin-top: 1rem;
		}

		span {
			width: auto;
			margin: 0;
			max-width: 4rem;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			display: inline-block;

			@media (min-width: $breakpoint-mobilelarge) {
				max-width: 10rem;
			}

			@media (min-width: $breakpoint-verysmall) {
				max-width: 17rem;
			}

			@media (min-width: $breakpoint-small) {
				max-width: max-content;
			}
		}

		a {
			display: inline;
			margin-left: 0.8rem;
			text-decoration: underline;
			font-weight: $font-weight-semibold;
			white-space: nowrap;
			cursor: pointer;

			&::before {
				content: '';
				position: relative;
				display: inline-block;
				background: $color-primary;
				border-radius: 100%;
				width: 0.3rem;
				height: 0.3rem;
				margin-right: 0.8rem;
				bottom: 0.25rem;
			}
		}
	}

	&__icon {
		position: relative;
		fill: $color-primary;
		margin-right: 0.4rem;
		margin-bottom: 0.25rem;
		top: 0.5rem;
		width: 1.6rem;
		height: 2.3rem;
	}

	&__ratings {
		margin-bottom: 0;

		:deep(.customer-reviews__rating) {
			width: auto;
		}

		:deep(.customer-reviews__rating-badge) {
			margin: 0;
		}
	}
}
</style>
